import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import i18n, { type Locale, DEFAULT_LOCALE } from "@/i18n";

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isRTL: false,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () => i18n.language as Locale
  );

  const isRTL = locale === "ar";

  // Sync document attributes on locale change
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [locale, isRTL]);

  const setLocale = (newLocale: Locale) => {
    if (newLocale === locale) return;
    localStorage.setItem("hirafi_lang", newLocale);
    i18n.changeLanguage(newLocale);
    setLocaleState(newLocale);
  };

  // Keep state in sync if i18n changes externally
  useEffect(() => {
    const handler = (lng: string) => {
      if (lng === "fr" || lng === "ar") {
        setLocaleState(lng as Locale);
      }
    };
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext() {
  return useContext(LanguageContext);
}
